import { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { useSidebar } from './SidebarContext';
import { useExternalAuth } from '../../services/externalAuthContext';
import { useMyExternalProjects } from '../../hooks/useMyExternalProjects';
import { SidebarProjectTree } from './SidebarProjectTree';
import type { SidebarProjectNode } from './SidebarProjectTree';

/**
 * Phase 23 D-01 / D-02 / v4.0 Landmine #8:
 *   - This component MUST NOT import the internal auth-context module or
 *     its hook. Doing so would couple the /external/* surface to the
 *     internal token state and re-introduce the Landmine #8 regression
 *     class.
 *   - Phase 23 D-02 originally scoped this component to exactly two
 *     affordances: recursive Projects tree + Sign out button. Phase 105
 *     (D-01) deliberately supersedes that count — it does not silently
 *     break it. As of Phase 105 there are three affordances: the
 *     recursive Projects tree (always), a Leads entry (only when the
 *     signed-in external user holds the sales-viewer grant surfaced by
 *     `GET /api/external/me`), and the Sign out button. See
 *     `docs/private/adr/0005-external-sales-viewer-grant-exposure.md` and the
 *     Phase 23 UI-SPEC amendment (Plan 105-05) for the record of this
 *     change. This file's own test (`__tests__/ExternalNavigation.test.tsx`,
 *     the first this component has ever had) pins both the gating rule and
 *     the isolation prohibition above.
 */
export function ExternalNavigation() {
  const { isCollapsed, toggle, mobileOpen, setMobileOpen } = useSidebar();
  const { logout, user } = useExternalAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Mobile-close-on-route-change (verbatim from Navigation.tsx:84-86 per UI-SPEC).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, setMobileOpen]);

  const projectsQuery = useMyExternalProjects();

  const treeData: SidebarProjectNode[] = (projectsQuery.data ?? []).map((p) => ({
    id: p.id,
    slug: p.slug, // Phase 27 D-15.4: slug for sidebar chip
    name: p.name,
    parentId: p.parentId,
    state: p.state,
    isMember: p.isMember,
  }));

  const handleSignOut = async () => {
    await logout();
    navigate('/external/login', { replace: true });
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <nav
        className={`fixed inset-y-0 left-0 z-50 bg-white shadow-lg flex flex-col transition-all duration-300 ${
          isCollapsed ? 'w-16' : 'w-64'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        {/* Header with brand + collapse toggle */}
        <div className="flex h-16 items-center justify-between border-b border-gray-200 px-4">
          {!isCollapsed && (
            <h1 className="text-xl font-bold text-primary-600">Monksheets</h1>
          )}
          <button
            type="button"
            onClick={toggle}
            className={`p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors ${
              isCollapsed ? 'mx-auto' : ''
            }`}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Body — Projects tree (always) + Leads (grant-gated, Phase 105) */}
        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col gap-1 p-4">
            {!isCollapsed && (
              <span className="px-3 py-2 text-xs font-semibold uppercase text-gray-400">
                Projects
              </span>
            )}
            {isCollapsed && <div className="border-t border-gray-200 pt-2" />}
            <SidebarProjectTree
              data={projectsQuery.data ? treeData : undefined}
              isLoading={projectsQuery.isLoading}
              isError={projectsQuery.isError}
              onRetry={() => projectsQuery.refetch()}
              routePrefix="/external/projects"
              expandedKey="monksheets:sidebar:external-projects:expanded"
              emptyCopy="You haven&apos;t been invited to any projects yet."
            />
          </div>

          {/* Phase 105 D-01: rendered only when the signed-in external user
              holds the sales-viewer grant. `user` is null while the initial
              /me round trip is in flight, so the optional-chained check
              fails closed — no flash of the entry before the grant is known. */}
          {user?.salesViewer && (
            <div className="flex flex-col gap-1 p-4 pt-0">
              <Link
                to="/external/leads"
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  location.pathname.startsWith('/external/leads')
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <CurrencyDollarIcon className="w-5 h-5 shrink-0" />
                {!isCollapsed && <span className="truncate">Leads</span>}
              </Link>
            </div>
          )}
        </div>

        {/* Sign-out at bottom — slim, no UserMenu.
            Phase 23 collapsed-mode rule: when sidebar is collapsed, hide the
            sign-out affordance entirely — matching how other text labels are
            hidden in collapsed mode. No Unicode back-arrow glyph. */}
        {!isCollapsed && (
          <div className="border-t border-gray-200 p-4">
            <button
              type="button"
              onClick={handleSignOut}
              className="text-sm font-medium text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded"
            >
              Sign out
            </button>
          </div>
        )}
      </nav>
    </>
  );
}
